import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { userService } from "../services/userService";

// API returns { user, affiliates, groups, grouP_DETAILS, settings, enableD_FEATURES }
// System.Text.Json CamelCase lowercases the leading uppercase run (keeping last char
// uppercase when followed by underscore). Single-word keys like USER → "user",
// but multi-word keys like GROUP_DETAILS → "grouP_DETAILS".
const selectProfile = (res) => {
  const body = res?.data ?? res;
  if (!body) return null;

  const user = body?.user ?? null;

  if (user) {
    const affiliates = body?.affiliates ?? [];
    const groups = body?.groups ?? [];
    const groupDetails = body?.grouP_DETAILS ?? [];
    const settings = body?.settings ?? null;
    const enabledFeatures = body?.enableD_FEATURES ?? [];

    // Build a convenience fullName
    const firstName = user.firsT_NAME ?? "";
    const lastName = user.lasT_NAME ?? "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ");

    return {
      ...user,
      fullName,
      AFFILIATES: affiliates,
      GROUPS: groups,
      GROUP_DETAILS: groupDetails,
      SETTINGS: settings,
      ENABLED_FEATURES: enabledFeatures,
    };
  }

  return body;
};

export const useProfile = () =>
  useQuery({
    queryKey: ["user", "profile"],
    queryFn: userService.getProfile,
    staleTime: 1000 * 60 * 5,
    select: selectProfile,
  });

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => userService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
    },
  });
};

export const useUserSettings = () =>
  useQuery({
    queryKey: ["user", "settings"],
    queryFn: userService.getSettings,
    staleTime: 1000 * 60 * 5,
    select: (res) => res?.data ?? res,
  });

export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => userService.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "settings"] });
    },
  });
};

export const useSetDefaultAffiliate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (affiliateCode) =>
      userService.setDefaultAffiliate(affiliateCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
      queryClient.invalidateQueries({ queryKey: ["posts", "feed"] });
      queryClient.invalidateQueries({ queryKey: ["sidebar"] });
      queryClient.invalidateQueries({ queryKey: ["contentCards"] });
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["discountCoupons"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["stories"] });
    },
  });
};
